// Local-date helpers. The app stores visit dates as 'YYYY-MM-DD' in the couple's local time (America/Chicago on their phones).
// Never use `new Date().toISOString()` for "today" (that is the UTC date) and never `new Date('YYYY-MM-DD')` to display one
// (that parses as UTC midnight and shows the previous day in the US).

const pad = (n) => String(n).padStart(2, '0');

/** 'YYYY-MM-DD' for a Date in local time. */
export function toLocalDateString(d) {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Today's local date as 'YYYY-MM-DD'. `now` is injectable for tests. */
export function todayLocal(now = new Date()) {
  return toLocalDateString(now);
}

/** Parse 'YYYY-MM-DD' as local midnight (a Date), or null when the string is not a date. */
export function parseLocalDate(s) {
  if (typeof s !== 'string') return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Local 'YYYY-MM-DD' of an ISO timestamp (e.g. a check's `date`) or of a 'YYYY-MM-DDTHH:MM' planned date. */
export function isoToLocalDate(iso) {
  if (typeof iso !== 'string') return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) return iso;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : toLocalDateString(d);
}

/** Whole local days between two 'YYYY-MM-DD' strings (b - a). */
export function daysBetween(a, b) {
  const da = parseLocalDate(a);
  const db = parseLocalDate(b);
  if (!da || !db) return null;
  return Math.round((db - da) / 86400000);
}

/** 'Sep 28, 2026' style label for a 'YYYY-MM-DD' date, in local time. */
export function formatLocalDate(s, options = { month: 'short', day: 'numeric', year: 'numeric' }) {
  const d = parseLocalDate(s);
  return d ? d.toLocaleDateString('en-US', options) : '';
}

/** Today / Yesterday / N days ago / N weeks ago / N months ago / N years ago, from a 'YYYY-MM-DD' to today's local date. */
export function daysAgoLabel(dateStr, now = new Date()) {
  const days = daysBetween(dateStr, todayLocal(now));
  if (days === null) return '';
  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days} days ago`;
  if (days < 30) return `${Math.floor(days / 7)} week${Math.floor(days / 7) === 1 ? '' : 's'} ago`;
  if (days < 365) return `${Math.floor(days / 30)} month${Math.floor(days / 30) === 1 ? '' : 's'} ago`;
  return `${Math.floor(days / 365)} year${Math.floor(days / 365) === 1 ? '' : 's'} ago`;
}
